import { useState, useEffect } from 'react';
import { casesApi } from '../lib/api';
import { generatePremiseTemplate, isAutoGeneratedPremise } from '../lib/premiseTemplates';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { X, FolderOpen, User, Car, MapPin, Home, Calendar, AlertCircle, Camera, Upload, Trash2, Tag, Building, CreditCard } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import InvestigatedPhotosManager from './InvestigatedPhotosManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const CASE_TYPES = [
    { value: 'infedelta_coniugale', label: 'Infedeltà coniugale' },
    { value: 'concorrenza_sleale', label: 'Concorrenza sleale' },
    { value: 'falsa_malattia', label: 'Falsa malattia' },
    { value: 'abuso_104', label: 'Abuso 104' },
    { value: 'altro', label: 'Altro' }
];

const GENDER_OPTIONS = [
    { value: 'M', label: 'Maschile' },
    { value: 'F', label: 'Femminile' }
];

export default function EditCaseModal({ caseData, onClose, onUpdated }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Copia locale del caso che si aggiorna su upload/delete foto senza chiudere il modal
    const [localCaseData, setLocalCaseData] = useState(caseData);
    
    // Protocol number - rimuovi il prefisso PROT- se presente per l'editing
    const existingProtocol = (caseData.protocol_number || '').replace(/^PROT-/, '');
    const [protocolNumber, setProtocolNumber] = useState(existingProtocol);
    
    // Case type
    const [caseType, setCaseType] = useState(caseData.case_type || '');
    
    // Client info
    const [clientName, setClientName] = useState(caseData.client_name || '');
    const [clientAddress, setClientAddress] = useState(caseData.client_address || '');
    const [clientEmail, setClientEmail] = useState(caseData.client_email || '');
    const [premises, setPremises] = useState(caseData.premises || '');
    const [status, setStatus] = useState(caseData.status || 'open');
    
    // Investigated person
    const [investigatedName, setInvestigatedName] = useState(caseData.investigated?.name || '');
    const [investigatedGender, setInvestigatedGender] = useState(caseData.investigated?.gender || '');
    const [investigatedVehicle, setInvestigatedVehicle] = useState(caseData.investigated?.vehicle || '');
    const [investigatedBirthDate, setInvestigatedBirthDate] = useState(caseData.investigated?.birth_date || '');
    const [investigatedBirthPlace, setInvestigatedBirthPlace] = useState(caseData.investigated?.birth_place || '');
    const [investigatedFiscalCode, setInvestigatedFiscalCode] = useState(caseData.investigated?.fiscal_code || '');
    const [investigatedAddress, setInvestigatedAddress] = useState(caseData.investigated?.address || '');
    const [investigatedDomicile, setInvestigatedDomicile] = useState(caseData.investigated?.domicile || '');
    const [workplaceCompany, setWorkplaceCompany] = useState(caseData.investigated?.workplace_company || '');
    const [workplaceAddress, setWorkplaceAddress] = useState(caseData.investigated?.workplace_address || '');
    
    // Social
    const [socialFacebook, setSocialFacebook] = useState(caseData.investigated?.social?.facebook || '');
    const [socialInstagram, setSocialInstagram] = useState(caseData.investigated?.social?.instagram || '');
    const [socialTiktok, setSocialTiktok] = useState(caseData.investigated?.social?.tiktok || '');
    const [socialLinkedin, setSocialLinkedin] = useState(caseData.investigated?.social?.linkedin || '');
    const [socialAltro, setSocialAltro] = useState(caseData.investigated?.social?.altro || '');
    
    // Photo
    const [currentPhotoUrl, setCurrentPhotoUrl] = useState(null);
    const [newPhoto, setNewPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoDeleted, setPhotoDeleted] = useState(false);
    
    // Additional info
    const [additionalInfo, setAdditionalInfo] = useState(caseData.additional_info || '');
    
    const token = localStorage.getItem('token');

    useEffect(() => {
        loadCurrentPhoto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-compila "premessa" in base a tipo indagine + nome/genere indagato.
    // Rispetta le modifiche manuali: sovrascrive solo se vuoto o se contiene
    // un template precedentemente auto-generato.
    useEffect(() => {
        const suggested = generatePremiseTemplate(caseType, investigatedName, investigatedGender);
        if (!suggested) return;
        if (premises.trim() === '' || isAutoGeneratedPremise(premises, investigatedName)) {
            setPremises(suggested);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseType, investigatedName, investigatedGender]);

    const loadCurrentPhoto = async () => {
        if (caseData.investigated_photo) {
            try {
                const response = await axios.get(
                    `${API_URL}/cases/${caseData.id}/investigated-photo/${caseData.investigated_photo.id}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                    }
                );
                const url = URL.createObjectURL(response.data);
                setCurrentPhotoUrl(url);
            } catch (err) {
                console.error('Error loading photo:', err);
            }
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setNewPhoto(file);
            setPhotoDeleted(false);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setNewPhoto(null);
        setPhotoPreview(null);
        setPhotoDeleted(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!protocolNumber) {
            setError('Il numero di protocollo è obbligatorio');
            return;
        }
        if (!clientName) {
            setError('Il nome del cliente è obbligatorio');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            // Aggiorna i dati del caso
            const response = await casesApi.update(caseData.id, {
                protocol_number: protocolNumber,
                case_type: caseType,
                client_name: clientName,
                client_address: clientAddress,
                client_email: clientEmail,
                premises: premises,
                status: status,
                investigated: {
                    name: investigatedName,
                    gender: investigatedGender,
                    vehicle: investigatedVehicle,
                    birth_date: investigatedBirthDate,
                    birth_place: investigatedBirthPlace,
                    fiscal_code: investigatedFiscalCode,
                    address: investigatedAddress,
                    domicile: investigatedDomicile,
                    workplace_company: workplaceCompany,
                    workplace_address: workplaceAddress,
                    social: {
                        facebook: socialFacebook,
                        instagram: socialInstagram,
                        tiktok: socialTiktok,
                        linkedin: socialLinkedin,
                        altro: socialAltro
                    }
                },
                additional_info: additionalInfo
            });
            
            // Gestisci la foto
            if (photoDeleted && caseData.investigated_photo) {
                // Elimina la foto esistente
                try {
                    await casesApi.deleteInvestigatedPhoto(caseData.id);
                } catch (err) {
                    console.error('Errore eliminazione foto:', err);
                }
            }
            
            if (newPhoto) {
                // Carica la nuova foto
                try {
                    await casesApi.uploadInvestigatedPhoto(caseData.id, newPhoto);
                } catch (err) {
                    console.error('Errore upload foto:', err);
                }
            }
            
            onUpdated(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'aggiornamento del caso');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 p-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                        <h2 className="font-heading text-lg font-bold text-white">Modifica Caso</h2>
                        <span className="font-mono text-[#00FF41] text-sm">{caseData.protocol_number || caseData.case_number}</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-white/60 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 text-sm">{error}</span>
                        </div>
                    )}

                    {/* Numero Protocollo e Tipologia */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 bg-[#00FF41]/5 border border-[#00FF41]/30 p-4">
                            <Label className="text-[#00FF41] uppercase text-xs font-bold">Numero Protocollo *</Label>
                            <div className="flex items-center gap-2">
                                <span className="text-[#00FF41] font-mono font-bold text-lg">PROT-</span>
                                <Input
                                    value={protocolNumber}
                                    onChange={(e) => setProtocolNumber(e.target.value)}
                                    placeholder="2024/001"
                                    required
                                    data-testid="edit-protocol-number-input"
                                    className="bg-black border-[#00FF41]/50 text-white font-mono focus:border-[#00FF41] rounded-none flex-1"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2 bg-[#00FF41]/5 border border-[#00FF41]/30 p-4">
                            <Label className="text-[#00FF41] uppercase text-xs font-bold flex items-center gap-1">
                                <Tag className="w-3 h-3" /> Tipologia
                            </Label>
                            <Select value={caseType} onValueChange={setCaseType}>
                                <SelectTrigger 
                                    data-testid="edit-case-type-select"
                                    className="bg-black border-[#00FF41]/50 text-white focus:border-[#00FF41] rounded-none"
                                >
                                    <SelectValue placeholder="Seleziona tipologia..." />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/20">
                                    {CASE_TYPES.map((type) => (
                                        <SelectItem 
                                            key={type.value} 
                                            value={type.value}
                                            className="text-white hover:bg-white/10"
                                        >
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                        <Label className="text-white/60 uppercase text-xs">Stato Caso</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger 
                                data-testid="case-status-select"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/20">
                                <SelectItem value="open" className="text-white hover:bg-white/10">
                                    Aperto
                                </SelectItem>
                                <SelectItem value="in_progress" className="text-white hover:bg-white/10">
                                    In Corso
                                </SelectItem>
                                <SelectItem value="closed" className="text-white hover:bg-white/10">
                                    Chiuso
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Client Info Section */}
                    <div className="space-y-4">
                        <h3 className="font-heading text-white flex items-center gap-2 border-b border-white/10 pb-2">
                            <User className="w-4 h-4 text-[#00FF41]" />
                            Informazioni Cliente
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white/60 uppercase text-xs">Nome Cliente *</Label>
                                <Input
                                    value={clientName}
                                    onChange={(e) => setClientName(e.target.value)}
                                    placeholder="Nome del cliente"
                                    required
                                    data-testid="edit-client-name-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs">Email</Label>
                                <Input
                                    type="email"
                                    value={clientEmail}
                                    onChange={(e) => setClientEmail(e.target.value)}
                                    placeholder="email@esempio.it"
                                    data-testid="edit-client-email-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Indirizzo</Label>
                            <Input
                                value={clientAddress}
                                onChange={(e) => setClientAddress(e.target.value)}
                                placeholder="Via, CAP, Città"
                                data-testid="edit-client-address-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                            />
                        </div>
                    </div>

                    {/* Premises */}
                    <div className="space-y-2">
                        <Label className="text-white/60 uppercase text-xs">Premessa</Label>
                        <Textarea
                            value={premises}
                            onChange={(e) => setPremises(e.target.value)}
                            placeholder="Descrivi il contesto e le motivazioni dell'indagine..."
                            rows={4}
                            data-testid="edit-premises-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none"
                        />
                    </div>

                    {/* Investigated Person Section */}
                    <div className="space-y-4">
                        <h3 className="font-heading text-white flex items-center gap-2 border-b border-white/10 pb-2">
                            <User className="w-4 h-4 text-[#00FF41]" />
                            Informazioni Indagato
                        </h3>
                        
                        {/* Foto Indagato - usa il componente InvestigatedPhotosManager */}
                        <div>
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1 mb-2">
                                <Camera className="w-3 h-3" /> Foto Indagato
                            </Label>
                            <InvestigatedPhotosManager 
                                caseId={localCaseData.id}
                                caseData={localCaseData}
                                isAdmin={true}
                                onUpdate={async () => {
                                    // Dopo upload/delete foto: ricarica il caso localmente
                                    // SENZA propagare al parent (evita la chiusura del modal).
                                    try {
                                        const response = await casesApi.get(localCaseData.id);
                                        setLocalCaseData(response.data);
                                    } catch (err) {
                                        console.error('Errore nel ricaricamento caso:', err);
                                    }
                                }}
                                showUpload={true}
                            />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <User className="w-3 h-3" /> Nome Indagato
                                </Label>
                                <Input
                                    value={investigatedName}
                                    onChange={(e) => setInvestigatedName(e.target.value)}
                                    placeholder="Nome completo"
                                    data-testid="edit-investigated-name-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <User className="w-3 h-3" /> Sesso
                                </Label>
                                <Select value={investigatedGender} onValueChange={setInvestigatedGender}>
                                    <SelectTrigger 
                                        data-testid="edit-investigated-gender-select"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    >
                                        <SelectValue placeholder="Seleziona..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0A0A] border-white/20">
                                        {GENDER_OPTIONS.map((opt) => (
                                            <SelectItem 
                                                key={opt.value} 
                                                value={opt.value}
                                                className="text-white hover:bg-white/10"
                                            >
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> Data di Nascita
                                </Label>
                                <Input
                                    type="date"
                                    value={investigatedBirthDate}
                                    onChange={(e) => setInvestigatedBirthDate(e.target.value)}
                                    data-testid="edit-investigated-birth-date-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> Luogo di Nascita
                                </Label>
                                <Input
                                    value={investigatedBirthPlace}
                                    onChange={(e) => setInvestigatedBirthPlace(e.target.value)}
                                    placeholder="Città di nascita"
                                    data-testid="edit-investigated-birth-place-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" /> Codice Fiscale
                                </Label>
                                <Input
                                    value={investigatedFiscalCode}
                                    onChange={(e) => setInvestigatedFiscalCode(e.target.value.toUpperCase())}
                                    placeholder="RSSMRA80A01H501U"
                                    maxLength={16}
                                    data-testid="edit-investigated-fiscal-code-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono uppercase"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> Indirizzo Residenza
                                </Label>
                                <Input
                                    value={investigatedAddress}
                                    onChange={(e) => setInvestigatedAddress(e.target.value)}
                                    placeholder="Via, CAP, Città"
                                    data-testid="edit-investigated-address-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <Home className="w-3 h-3" /> Domicilio
                                </Label>
                                <Input
                                    value={investigatedDomicile}
                                    onChange={(e) => setInvestigatedDomicile(e.target.value)}
                                    placeholder="Se diverso dalla residenza"
                                    data-testid="edit-investigated-domicile-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <Car className="w-3 h-3" /> Automezzo
                                </Label>
                                <Input
                                    value={investigatedVehicle}
                                    onChange={(e) => setInvestigatedVehicle(e.target.value)}
                                    placeholder="Marca, modello, targa"
                                    data-testid="edit-investigated-vehicle-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                        </div>
                        
                        {/* Luogo di Lavoro */}
                        <div className="space-y-3 pt-3 border-t border-white/10">
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                <Building className="w-3 h-3" /> Luogo di Lavoro
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white/40 text-xs">Azienda</Label>
                                    <Input
                                        value={workplaceCompany}
                                        onChange={(e) => setWorkplaceCompany(e.target.value)}
                                        placeholder="Nome azienda"
                                        data-testid="edit-workplace-company-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs">Indirizzo</Label>
                                    <Input
                                        value={workplaceAddress}
                                        onChange={(e) => setWorkplaceAddress(e.target.value)}
                                        placeholder="Indirizzo sede lavoro"
                                        data-testid="edit-workplace-address-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Social Network */}
                        <div className="space-y-3 pt-3 border-t border-white/10">
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                Social Network
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-blue-600 rounded text-white text-[10px] flex items-center justify-center font-bold">f</span>
                                        Facebook
                                    </Label>
                                    <Input
                                        value={socialFacebook}
                                        onChange={(e) => setSocialFacebook(e.target.value)}
                                        placeholder="username o URL"
                                        data-testid="edit-social-facebook-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 rounded text-white text-[10px] flex items-center justify-center font-bold">I</span>
                                        Instagram
                                    </Label>
                                    <Input
                                        value={socialInstagram}
                                        onChange={(e) => setSocialInstagram(e.target.value)}
                                        placeholder="@username o URL"
                                        data-testid="edit-social-instagram-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-black border border-white/30 rounded text-white text-[10px] flex items-center justify-center font-bold">T</span>
                                        TikTok
                                    </Label>
                                    <Input
                                        value={socialTiktok}
                                        onChange={(e) => setSocialTiktok(e.target.value)}
                                        placeholder="@username o URL"
                                        data-testid="edit-social-tiktok-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-blue-700 rounded text-white text-[10px] flex items-center justify-center font-bold">in</span>
                                        LinkedIn
                                    </Label>
                                    <Input
                                        value={socialLinkedin}
                                        onChange={(e) => setSocialLinkedin(e.target.value)}
                                        placeholder="username o URL"
                                        data-testid="edit-social-linkedin-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label className="text-white/40 text-xs">Altro (altri social o note)</Label>
                                    <Input
                                        value={socialAltro}
                                        onChange={(e) => setSocialAltro(e.target.value)}
                                        placeholder="Altri profili social, note..."
                                        data-testid="edit-social-altro-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-2">
                        <Label className="text-white/60 uppercase text-xs">Informazioni Aggiuntive</Label>
                        <Textarea
                            value={additionalInfo}
                            onChange={(e) => setAdditionalInfo(e.target.value)}
                            placeholder="Altre informazioni rilevanti..."
                            rows={3}
                            data-testid="edit-additional-info-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none"
                        />
                    </div>

                    {/* Nota per assegnazione agenti */}
                    <div className="bg-blue-500/10 border border-blue-500/30 p-3">
                        <p className="text-blue-400 text-sm flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Per assegnare o modificare gli agenti, usa la sezione <strong>"Gestione Caso"</strong> nella pagina del caso, dove puoi selezionare i giorni di lavoro specifici.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-white/20 text-white hover:bg-white/10 rounded-none"
                        >
                            ANNULLA
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-edit-case-btn"
                            className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                        >
                            {loading ? 'SALVATAGGIO...' : 'SALVA MODIFICHE'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
